import React, { useEffect, useMemo, useState } from 'react'
import { Search, X } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { StatusBadge } from '@/components/ui/StatusBadge'
import { formatVnNumber } from '@/features/purchasing/poWorkflow'
import type { InventoryItemSummary, KpiGroupKey } from '@/services/reports/inventoryDashboardApi'

export interface KpiDrilldownModalProps {
  isOpen: boolean
  onClose: () => void
  title: string
  items: InventoryItemSummary[]
  kpiKey: KpiGroupKey | null
}

export function KpiDrilldownModal({
  isOpen,
  onClose,
  title,
  items,
  kpiKey,
}: KpiDrilldownModalProps): React.ReactElement | null {
  const [searchTerm, setSearchTerm] = useState('')

  // Filter items based on KPI group
  const groupFilteredItems = useMemo(() => {
    if (!kpiKey || kpiKey === 'all') return items

    switch (kpiKey) {
      case 'physical_stock':
        return items.filter((i) => i.physical_stock > 0)
      case 'available_stock':
        return items.filter((i) => i.available_stock > 0)
      case 'active_hold':
        return items.filter((i) => i.reserved_stock > 0)
      case 'out_of_stock':
        return items.filter((i) => i.physical_stock === 0)
      case 'below_min':
        return items.filter((i) => i.physical_stock > 0 && i.available_stock <= i.min_stock)
      case 'slow_moving':
        return items.filter((i) => i.days_inactive > 30)
      case 'unpriced':
        return items.filter((i) => i.unit_price == null)
      default:
        return items
    }
  }, [items, kpiKey])

  // Filter by search query
  const displayItems = useMemo(() => {
    if (!searchTerm.trim()) return groupFilteredItems
    const term = searchTerm.toLowerCase()
    return groupFilteredItems.filter(
      (item) =>
        item.material_code.toLowerCase().includes(term) ||
        item.material_name.toLowerCase().includes(term) ||
        item.category_name.toLowerCase().includes(term),
    )
  }, [groupFilteredItems, searchTerm])

  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape' && isOpen) {
        onClose()
      }
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [isOpen, onClose])

  if (!isOpen) return null

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="kpi-drilldown-title"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-3 sm:p-4 backdrop-blur-xs"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose()
      }}
    >
      <div className="flex max-h-[85vh] w-full max-w-4xl flex-col rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-[var(--color-border)] px-4 py-3 sm:px-6">
          <div className="flex items-center gap-2">
            <h2 id="kpi-drilldown-title" className="text-base font-semibold text-[var(--color-text)]">
              {title}
            </h2>
            <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-semibold text-slate-700">
              {displayItems.length} vật tư
            </span>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Đóng"
            className="rounded p-1 text-[var(--color-text-muted)] hover:bg-slate-100 hover:text-[var(--color-text)]"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Toolbar: Search input */}
        <div className="border-b border-[var(--color-border)] px-4 py-2 sm:px-6 bg-slate-50/60">
          <div className="relative max-w-sm">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-[var(--color-text-muted)]" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Tìm kiếm theo mã, tên vật tư..."
              className="h-9 w-full rounded border border-[var(--color-border)] bg-white pl-8 pr-3 text-sm focus:border-[var(--color-accent)] focus:outline-hidden"
            />
          </div>
        </div>

        {/* Body: Responsive Table */}
        <div className="flex-1 overflow-auto p-4 sm:p-6">
          {displayItems.length === 0 ? (
            <div className="py-12 text-center text-sm text-[var(--color-text-muted)]">
              Không tìm thấy vật tư nào phù hợp.
            </div>
          ) : (
            <div className="overflow-x-auto rounded border border-[var(--color-border)]">
              <table className="min-w-full divide-y divide-[var(--color-border)] text-left text-sm">
                <thead className="bg-slate-50 text-xs font-semibold uppercase tracking-wider text-[var(--color-text-muted)]">
                  <tr>
                    <th scope="col" className="px-3 py-2.5 text-center w-12">
                      STT
                    </th>
                    <th scope="col" className="px-3 py-2.5">
                      Mã vật tư
                    </th>
                    <th scope="col" className="px-3 py-2.5">
                      Tên vật tư
                    </th>
                    <th scope="col" className="px-3 py-2.5">
                      Nhóm
                    </th>
                    <th scope="col" className="px-3 py-2.5 text-center w-16">
                      ĐVT
                    </th>
                    <th scope="col" className="px-3 py-2.5 text-right">
                      Tồn thực tế
                    </th>
                    <th scope="col" className="px-3 py-2.5 text-right">
                      Tồn khả dụng
                    </th>
                    <th scope="col" className="px-3 py-2.5 text-right">
                      Đang giữ
                    </th>
                    <th scope="col" className="px-3 py-2.5 text-right">
                      Định mức / Giá
                    </th>
                    <th scope="col" className="px-3 py-2.5 text-center">
                      Trạng thái
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[var(--color-border)] bg-white">
                  {displayItems.map((item, index) => {
                    const isOutOfStock = item.physical_stock === 0
                    const isBelowMin = !isOutOfStock && item.available_stock <= item.min_stock
                    const statusTone = isOutOfStock
                      ? 'danger'
                      : isBelowMin
                      ? 'warning'
                      : item.unit_price == null
                      ? 'neutral'
                      : 'success'

                    return (
                      <tr
                        key={item.id}
                        className={`hover:bg-slate-50/80 ${
                          isOutOfStock ? 'bg-red-50/30' : isBelowMin ? 'bg-amber-50/20' : ''
                        }`}
                      >
                        <td className="px-3 py-2 text-center text-xs text-[var(--color-text-muted)]">
                          {index + 1}
                        </td>
                        <td className="px-3 py-2 font-mono text-xs font-semibold text-slate-800">
                          {item.material_code}
                        </td>
                        <td className="px-3 py-2 font-medium text-slate-900">
                          {item.material_name}
                        </td>
                        <td className="px-3 py-2 text-xs text-[var(--color-text-muted)]">
                          {item.category_name}
                        </td>
                        <td className="px-3 py-2 text-center text-xs text-slate-600">
                          {item.uom}
                        </td>
                        <td
                          className={`px-3 py-2 text-right font-medium tabular-nums ${
                            isOutOfStock ? 'text-red-600 font-semibold' : 'text-slate-800'
                          }`}
                        >
                          {formatVnNumber(item.physical_stock)}
                        </td>
                        <td
                          className={`px-3 py-2 text-right font-medium tabular-nums ${
                            item.available_stock <= 0
                              ? 'text-red-600'
                              : isBelowMin
                              ? 'text-amber-700 font-semibold'
                              : 'text-emerald-700'
                          }`}
                        >
                          {formatVnNumber(item.available_stock)}
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums text-slate-600">
                          {item.reserved_stock > 0 ? (
                            <span className="font-semibold text-amber-600">
                              {formatVnNumber(item.reserved_stock)}
                            </span>
                          ) : (
                            '0'
                          )}
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums text-xs text-slate-600">
                          <div>Min: {formatVnNumber(item.min_stock)}</div>
                          <div className="text-[11px] text-[var(--color-text-muted)]">
                            {item.unit_price != null
                              ? `${formatVnNumber(item.unit_price)} ₫`
                              : 'Chưa có giá'}
                          </div>
                        </td>
                        <td className="px-3 py-2 text-center">
                          <StatusBadge tone={statusTone} label={item.status_label} />
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between border-t border-[var(--color-border)] px-4 py-3 sm:px-6">
          <div className="text-xs text-[var(--color-text-muted)]">
            Hiển thị {displayItems.length} / {groupFilteredItems.length} vật tư
          </div>
          <Button variant="secondary" size="sm" onClick={onClose}>
            Đóng
          </Button>
        </div>
      </div>
    </div>
  )
}
